import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSessionStore } from '../../store/sessionStore.js';
import { pwaApi } from '../../lib/api.js';
import FermoModal from '../../components/FermoModal.js';
import ConfirmDialog from '../../components/ConfirmDialog.js';
import OrderWizard, { type ProductionOrderOption, type PlantUnitOption } from '../../components/OrderWizard.js';
import { GRID_16, at } from '../../lib/layout.js';

interface RateReading {
  id: number;
  value: string;
  recordedAt: string;
}

/**
 * Stato usato dal tasto AVVIO MACCHINA (decisione Mario 24/07/2026).
 * Valore e nome categoria aggiornati il 07/09/2026: la migrazione di riscrittura Montaigu del
 * 31/08/2026 ha rinominato in-place "Macchina in produzione" → "Macchina in Lavorazione" e la
 * categoria "In produzione" → "In lavorazione" (stesso id, vedi ADR "Catalogo stati macchina —
 * riscrittura Montaigu"), rompendo questo confronto testuale: AVVIO restava sempre disabilitato.
 */
const START_STATE_VALUE_IT = 'Macchina in Lavorazione';

const TILE = 'rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 flex flex-col justify-center min-w-0';
// Alto quanto il contenuto, non quanto la riga: prima il pannello si allungava a riempire
// tutto lo spazio verticale disponibile (flex-1 sul contenitore), lasciando una fascia vuota
// sotto ai campi su schermi più alti del necessario — segnalato da Mario il 04/08/2026.
// max-h-full/overflow-y-auto restano come rete di sicurezza sui tablet più bassi (contenuto
// che non ci sta diventa scrollabile invece di essere tagliato senza preavviso).
const PANEL = 'rounded-lg border border-gray-700 bg-gray-900 p-4 flex flex-col gap-3 max-h-full overflow-y-auto';
const BOX = 'rounded-lg border border-gray-700 bg-gray-800/60 px-4 py-3 flex flex-col justify-center min-w-0';
const INPUT = 'w-full bg-gray-800 text-white px-3 py-3 rounded border border-gray-600 focus:border-blue-500 focus:outline-none text-lg';
const LABEL = 'text-gray-400 text-[11px] font-bold uppercase tracking-wide';
// Bersaglio ampio: la PWA si usa a bordo macchina, spesso con i guanti
const ACTION = 'min-h-[4rem] px-4 rounded-lg font-bold text-base uppercase leading-tight text-white disabled:bg-gray-700 disabled:text-gray-500';
// Bordo + ombra: senza, i pulsanti pieni (rosso/verde) si confondono con lo sfondo quando
// prende il colore dello stato macchina corrente (es. CHIUSURA rosso su sfondo rosso di un fermo)
const SOLID_BUTTON = 'border-2 border-white/70 shadow-lg shadow-black/40';
const HISTORY_BUTTON = 'min-h-[3.5rem] px-3 py-2 rounded-lg font-bold text-xs uppercase leading-tight bg-gray-700 hover:bg-gray-600 active:bg-gray-500 disabled:bg-gray-800 disabled:text-gray-600 text-white border border-gray-600';

export default function WorkstationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    availableStates, sessionId, isSessionActive, currentOrderUdm, currentOrderQuantity,
    operators, currentStateLog, productionSummary,
    refreshProductionSummary, refreshOperators, endSession, addStateLog,
  } = useSessionStore();

  const [orders, setOrders] = useState<ProductionOrderOption[]>([]);
  const [plantUnits, setPlantUnits] = useState<PlantUnitOption[]>([]);
  const [rateReadings, setRateReadings] = useState<RateReading[]>([]);

  const [pouredValue, setPouredValue] = useState('');
  const [rejectValue, setRejectValue] = useState('');
  const [speedValue, setSpeedValue] = useState('');

  const [wizardOpen, setWizardOpen] = useState(false);
  const [fermoOpen, setFermoOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ message: string; onConfirm: () => void } | null>(null);

  async function loadOrders() {
    const { data } = await pwaApi.get('/tablet/production-orders');
    setOrders(data.data as ProductionOrderOption[]);
  }

  useEffect(() => { loadOrders().catch(() => {}); }, []);

  useEffect(() => {
    pwaApi.get('/tablet/measurement-units')
      .then(({ data }) => setPlantUnits(data.data as PlantUnitOption[]))
      .catch(() => {});
  }, []);

  async function loadRateReadings() {
    if (!sessionId) return setRateReadings([]);
    const { data } = await pwaApi.get(`/tablet/sessions/${sessionId}/production`, { params: { type: 'RATE', limit: 1 } });
    setRateReadings(data.data as RateReading[]);
  }

  useEffect(() => { loadRateReadings().catch(() => {}); }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { refreshOperators().catch(() => {}); }, [sessionId, refreshOperators]);

  const currentSpeed = rateReadings[0]?.value ?? null;
  const loggedOperators = operators.filter(o => o.logoutAt === null).map(o => o.operatorNumber);

  // Stato "in produzione" per il tasto AVVIO: match esatto sul valore italiano, che è il
  // master dell'anagrafica (valueLocale cambia con la lingua del tablet).
  const startState = useMemo(
    () => availableStates.find(s => s.valueIt === START_STATE_VALUE_IT)
      ?? availableStates.find(s => s.subcategory.category.valueIt === 'In lavorazione')
      ?? null,
    [availableStates],
  );
  // Bug segnalato da Mario il 30/07/2026: AVVIO restava sempre attivo, anche a macchina già in
  // produzione, perché non veniva mai confrontato con lo stato corrente. FERMO invece non ha un
  // singolo "stato di arrivo" (apre la cascata con decine di motivi), quindi resta sempre visibile.
  const alreadyRunning = currentStateLog !== null && currentStateLog.machineState.id === startState?.id;

  async function postProduction(type: 'PRODUCED' | 'REJECT', value: number) {
    if (!sessionId) return;
    await pwaApi.post(`/tablet/sessions/${sessionId}/production`, { type, value });
    await refreshProductionSummary();
  }

  async function handlePour() {
    if (!pouredValue) return;
    await postProduction('PRODUCED', Number(pouredValue));
    setPouredValue('');
  }

  async function handleReject() {
    if (!rejectValue) return;
    await postProduction('REJECT', Number(rejectValue));
    setRejectValue('');
  }

  async function handleSpeedChange() {
    if (!sessionId || !speedValue) return;
    await pwaApi.post(`/tablet/sessions/${sessionId}/production`, { type: 'RATE', value: Number(speedValue) });
    setSpeedValue('');
    await loadRateReadings();
  }

  async function handleCloseOrder() {
    await endSession();
    await loadOrders();
    setRateReadings([]);
  }

  function requestCloseOrder() {
    setConfirmAction({ message: t('confirm.closeOrder'), onConfirm: () => { setConfirmAction(null); void handleCloseOrder(); } });
  }

  function requestPour() {
    setConfirmAction({ message: t('confirm.quantity'), onConfirm: () => { setConfirmAction(null); void handlePour(); } });
  }

  function requestReject() {
    setConfirmAction({ message: t('confirm.quantity'), onConfirm: () => { setConfirmAction(null); void handleReject(); } });
  }

  const disabled = !isSessionActive;
  const udmSuffix = currentOrderUdm ? ` ${currentOrderUdm}` : '';

  return (
    <div className="h-full flex flex-col gap-3 p-3 overflow-hidden">

      {/* ── riga 2 — operatori | velocità settata | apertura/chiusura ordine (nr. ordine ora in */}
      {/* header, sempre visibile). Il tasto apertura/chiusura era su una riga propria: spostato */}
      {/* qui il 30/07/2026 su richiesta di Mario, per recuperare spazio verticale sul 10.1" ── */}
      <div className={`${GRID_16} shrink-0`}>
        <button
          style={at(1, 4)}
          onClick={() => navigate('/workstation/operators')}
          className={`${TILE} text-left hover:bg-gray-800 active:bg-gray-700 transition-colors`}
        >
          <span className={LABEL}>{t('shell.operators')}</span>
          <span className="text-white text-xl font-bold truncate">
            {loggedOperators.length > 0
              ? loggedOperators.map(n => `${t('operator.short')}${n}`).join(' · ')
              : t('shell.noOperator')}
          </span>
        </button>

        <div style={at(11, 2)} className={TILE}>
          <span className={LABEL}>{t('speed.set')}</span>
          <span className="text-white text-2xl font-bold truncate">{currentSpeed ?? '—'}</span>
        </div>

        {isSessionActive ? (
          <button style={at(14, 3)} onClick={requestCloseOrder} className={`${ACTION} ${SOLID_BUTTON} bg-red-700 hover:bg-red-600 active:bg-red-500`}>
            {t('order.close')}
          </button>
        ) : (
          <button style={at(14, 3)} onClick={() => setWizardOpen(true)} className={`${ACTION} ${SOLID_BUTTON} bg-green-600 hover:bg-green-700 active:bg-green-500`}>
            {t('order.open')}
          </button>
        )}
      </div>

      {/* ── riga 3 — pannello quantità | pannello velocità. `items-start` (non lo stretch di */}
      {/* default della grid) impedisce ai due PANEL di allungarsi oltre il proprio contenuto ── */}
      <div className={`${GRID_16} items-start shrink-0 min-h-0`}>

        <div style={at(1, 8)} className={PANEL}>
          <div className="grid grid-cols-8 gap-3">
            <div className="col-span-3">
              <div className={LABEL}>{t('order.quantityOrdered')}</div>
              <div className="text-white text-2xl font-bold truncate">
                {currentOrderQuantity ?? '—'}{currentOrderQuantity ? udmSuffix : ''}
              </div>
            </div>
            <div className="col-span-3">
              <div className={LABEL}>{t('summary.produced')}</div>
              <div className="text-white text-2xl font-bold truncate">
                {productionSummary.producedTotal}{currentOrderUdm ? ` ${currentOrderUdm}` : ''}
              </div>
            </div>
            <div className="col-span-2">
              <div className={LABEL}>{t('summary.rejects')}</div>
              <div className="text-white text-2xl font-bold truncate">
                {productionSummary.rejectTotal}{currentOrderUdm ? ` ${currentOrderUdm}` : ''}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-8 gap-3 items-end">
            <div className="col-span-3">
              <div className={`${LABEL} mb-1`}>
                {t('production.rejectButton')}{currentOrderUdm ? ` (${currentOrderUdm})` : ''}
              </div>
              <input className={INPUT} type="number" value={rejectValue} disabled={disabled} onChange={e => setRejectValue(e.target.value)} />
            </div>
            <button
              onClick={requestReject}
              disabled={disabled || !rejectValue}
              className={`col-span-3 ${ACTION} bg-blue-900 hover:bg-blue-800 active:bg-blue-700`}
            >
              {t('production.rejectButton')}
            </button>
            <button onClick={() => navigate('/workstation/production/REJECT')} disabled={disabled} className={`col-span-2 ${HISTORY_BUTTON}`}>
              {t('order.history')}<br />{t('nav.rejects')}
            </button>
          </div>

          <div className="grid grid-cols-8 gap-3 items-end">
            <div className="col-span-3">
              <div className={`${LABEL} mb-1`}>
                {t('production.poured')}{currentOrderUdm ? ` (${currentOrderUdm})` : ''}
              </div>
              <input className={INPUT} type="number" value={pouredValue} disabled={disabled} onChange={e => setPouredValue(e.target.value)} />
            </div>
            <button
              onClick={requestPour}
              disabled={disabled || !pouredValue}
              className={`col-span-3 ${ACTION} bg-blue-600 hover:bg-blue-500 active:bg-blue-400`}
            >
              {t('production.pourButton')}
            </button>
            <button onClick={() => navigate('/workstation/production/PRODUCED')} disabled={disabled} className={`col-span-2 ${HISTORY_BUTTON}`}>
              {t('order.history')}<br />{t('nav.producedPieces')}
            </button>
          </div>
        </div>

        <div style={at(9, 8)} className={PANEL}>
          <div className="grid grid-cols-8 gap-3">
            <div className="col-span-5 col-start-2">
              <div className={BOX}>
                <span className={LABEL}>{t('speed.current')}</span>
                <span className="text-white text-3xl font-bold">{currentSpeed ?? '—'}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-8 gap-3 items-end">
            <div className="col-span-3">
              <div className={`${LABEL} mb-1`}>{t('speed.new')}</div>
              <input className={INPUT} type="number" value={speedValue} disabled={disabled} onChange={e => setSpeedValue(e.target.value)} />
            </div>
            <button
              onClick={handleSpeedChange}
              disabled={disabled || !speedValue}
              className={`col-span-3 ${ACTION} bg-cyan-600 hover:bg-cyan-500 active:bg-cyan-400`}
            >
              {t('speed.confirm')}
            </button>
            <button onClick={() => navigate('/workstation/production/RATE')} disabled={disabled} className={`col-span-2 ${HISTORY_BUTTON}`}>
              {t('order.history')}<br />{t('nav.speed')}
            </button>
          </div>

          <div className="grid grid-cols-8 gap-3">
            <button onClick={() => navigate('/workstation/states')} disabled={disabled} className={`col-span-3 ${HISTORY_BUTTON}`}>
              {t('order.history')}<br />{t('nav.viewStates')}
            </button>
          </div>
        </div>
      </div>

      {/* ── riga 4 — FERMO | AVVIO (nascosto quando la macchina è già in produzione) ── */}
      <div className={`${GRID_16} shrink-0`}>
        <button
          style={at(1, 7)}
          onClick={() => setFermoOpen(true)}
          disabled={disabled || availableStates.length === 0}
          className={`${SOLID_BUTTON} bg-red-700 hover:bg-red-600 active:bg-red-500 disabled:bg-gray-700 disabled:border-transparent text-white font-bold text-2xl py-6 rounded-lg transition-colors`}
        >
          {t('state.fermo')}
        </button>
        {!alreadyRunning && (
          <button
            style={at(9, 8)}
            onClick={() => { if (startState) void addStateLog(startState.id); }}
            disabled={disabled || !startState}
            className={`${SOLID_BUTTON} bg-green-600 hover:bg-green-500 active:bg-green-400 disabled:bg-gray-700 disabled:border-transparent text-white font-bold text-2xl py-6 rounded-lg transition-colors`}
          >
            {t('state.avvio')}
          </button>
        )}
      </div>

      {wizardOpen && (
        <OrderWizard
          orders={orders}
          plantUnits={plantUnits}
          onClose={() => { setWizardOpen(false); void loadRateReadings(); }}
          onOrdersChanged={loadOrders}
        />
      )}

      {fermoOpen && (
        <FermoModal
          states={availableStates}
          onConfirm={(id, note) => addStateLog(id, note)}
          onClose={() => setFermoOpen(false)}
        />
      )}

      {confirmAction && (
        <ConfirmDialog
          message={confirmAction.message}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}
